/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import {
    FlexRow,
    FlexCol,
    Button,
    TextField,
    Select,
    Switch,
    useFlags,
    SecurityHelper,
} from 'sarsaparilla';
import CreditCard from 'shared-ui/components/CreditCard';
import { ALLOWED_WALLET_CARD_ISSUERS } from '../../constants';

import { formatCreditCardNumber, formatCVC } from '../../utils/cart_utils';
import { months, years } from './NewCreditCardForm.utils';
import { makeSuggestedSavedCardNickname } from './SavedCreditCardCard.utils';

export type PRSPaymentPageState = {
    number: string;
    firstName: string;
    lastName: string;
    month: string;
    year: string;
    cvc: string;
    issuer: string;
    focused: string;
    nickname: string;
    shouldSaveCard: boolean;
    selectedSavedCardToken: string;
    preview: boolean;
};

export interface NewCreditCardFormProps {
    showPurchaseGiftCard: boolean;
    isWalletUnavailable?: boolean;
    hasMaxSavedCards?: boolean;
    updateParentState: (state: Partial<PRSPaymentPageState>) => void;
}

export function NewCreditCardForm({
    showPurchaseGiftCard,
    updateParentState,
    isWalletUnavailable,
    hasMaxSavedCards,
}: NewCreditCardFormProps) {
    const [focused, setFocused] = React.useState('');
    const [creditCardNumber, setCreditCardNumber] = React.useState('');
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [month, setMonth] = React.useState('');
    const [year, setYear] = React.useState('');
    const [cvc, setCvc] = React.useState('');
    const [nickname, setNickname] = React.useState('My Credit Card');
    const [issuer, setIssuer] = React.useState('');
    const [shouldSaveCard, setShouldSaveCard] = React.useState(false);
    const [isValid, setIsValid] = React.useState(false);
    const [hasCustomizedNickname, setHasCustomizedNickname] = React.useState(false);
    const { enableDigitalWallet } = useFlags();
    const isCSR = SecurityHelper.isExternalCSRLoggedIn();
    const issuerIsNotAllowed = !ALLOWED_WALLET_CARD_ISSUERS.includes(issuer);

    // User is only allowed to see the "Save card" toggle if the feature flag is on,
    // the token API didn't return an error, the user has fewer than the max number
    // of saved cards, and they are not a CSR impersonating a user
    const canSeeSaveCardToggle =
        enableDigitalWallet && !isWalletUnavailable && !hasMaxSavedCards && !isCSR;

    const disableNextButton =
        !isValid ||
        firstName === '' ||
        lastName === '' ||
        month === '' ||
        year === '' ||
        cvc === '';

    const onInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        setFocused(event.target.name);
    };

    const onNextClick = () => {
        updateParentState({
            number: creditCardNumber,
            firstName,
            lastName,
            month,
            year,
            cvc,
            issuer,
            nickname,
            shouldSaveCard,
            preview: true,
        });
    };

    // Construct a suggest nickname as the user completes the form
    React.useEffect(() => {
        if (!hasCustomizedNickname) {
            setNickname(
                makeSuggestedSavedCardNickname({ issuer, creditCardNumber, isValid })
            );
        }
    }, [creditCardNumber, hasCustomizedNickname, isValid, issuer]);

    return (
        <FlexRow>
            <FlexCol
                sm={12}
                lg={6}
                xl={5}
                className="mb-3 data-hj-suppress"
                translate="no"
            >
                <CreditCard
                    number={creditCardNumber}
                    name={`${firstName} ${lastName}`}
                    expiry={`${month}/${year}`}
                    cvc={cvc}
                    focused={focused}
                    callback={(values, valid) => {
                        if (values.issuer !== 'unknown') {
                            setIssuer(values.issuer);
                        }
                        setIsValid(valid);
                    }}
                />
            </FlexCol>
            <FlexCol sm={12} lg={6} xl={7}>
                <FlexRow className="rec-cart-form-field">
                    <FlexCol>
                        <TextField
                            label="First Name"
                            isRequired
                            id="first-name"
                            name="firstName"
                            className="mt-half"
                            onChange={(event) => {
                                setFirstName(event.currentTarget.value);
                            }}
                            onFocus={onInputFocus}
                            value={firstName}
                        />
                    </FlexCol>
                    <FlexCol>
                        <TextField
                            label="Last Name"
                            isRequired
                            id="last-name"
                            name="lastName"
                            className="mt-half"
                            onChange={(event) => {
                                setLastName(event.currentTarget.value);
                            }}
                            onFocus={onInputFocus}
                            value={lastName}
                        />
                    </FlexCol>
                </FlexRow>
                <FlexRow className="rec-cart-form-field">
                    <FlexCol>
                        <TextField
                            isRequired
                            label="Credit Card Number"
                            id="number"
                            name="number"
                            className="mt-half"
                            type="tel"
                            pattern="[\d ]{16,19}"
                            maxLength={22}
                            value={creditCardNumber}
                            onChange={(event) => {
                                const value = formatCreditCardNumber(
                                    event.currentTarget.value
                                );
                                setCreditCardNumber(value);
                            }}
                            onFocus={onInputFocus}
                        />
                    </FlexCol>
                </FlexRow>
                <FlexRow className="rec-cart-form-field">
                    <FlexCol>
                        <Select
                            label="Month"
                            isRequired
                            id="month"
                            name="month"
                            className="mt-half"
                            value={month}
                            onChange={(event) => {
                                setMonth(event.currentTarget.value);
                            }}
                            onFocus={() => {
                                setFocused('expiry');
                            }}
                            placeholder="MM"
                            options={months.map((m) => ({
                                label: m,
                                value: m,
                            }))}
                        />
                    </FlexCol>
                    <FlexCol>
                        <Select
                            label="Year"
                            isRequired
                            id="year"
                            className="mt-half"
                            name="year"
                            value={year}
                            onChange={(event) => {
                                setYear(event.currentTarget.value);
                            }}
                            onFocus={() => {
                                setFocused('expiry');
                            }}
                            placeholder="YYYY"
                            options={years.map((y) => ({
                                label: `20${y}`,
                                value: y,
                            }))}
                        />
                    </FlexCol>
                </FlexRow>
                <FlexRow className="rec-cart-form-field">
                    <FlexCol>
                        <TextField
                            label="Security Code"
                            isRequired
                            id="cvc"
                            className="mt-half"
                            type="tel"
                            name="cvc"
                            pattern="\d{3,4}"
                            value={cvc}
                            onChange={(event) => {
                                const value = formatCVC(event.currentTarget.value);
                                setCvc(value);
                            }}
                            onFocus={onInputFocus}
                        />
                    </FlexCol>
                </FlexRow>

                {canSeeSaveCardToggle ? (
                    <div className="my-3">
                        <Switch
                            label="Save Card for Future Use"
                            description={
                                issuer && issuerIsNotAllowed
                                    ? 'Sorry, this type of card can not be saved'
                                    : 'You can remove or update your card later from Account'
                            }
                            name="saveCard"
                            onChange={setShouldSaveCard}
                            isSelected={shouldSaveCard}
                            isDisabled={issuer ? issuerIsNotAllowed : false}
                        />

                        {shouldSaveCard ? (
                            <div className="mt-3">
                                <TextField
                                    label="Add an Nickname for this Card"
                                    id="nickname"
                                    name="nickname"
                                    value={nickname}
                                    onChange={(event) => {
                                        setNickname(event.currentTarget.value);
                                        setHasCustomizedNickname(true);
                                    }}
                                    maxLength={50}
                                />
                            </div>
                        ) : null}
                    </div>
                ) : null}

                <input type="hidden" name="issuer" value={issuer} />

                <Button
                    isDisabled={disableNextButton}
                    onClick={onNextClick}
                    appearance="primary"
                    size="lg"
                    className="mb-2"
                    gaTrackingId="753434423094"
                    gaTrackingValue={
                        shouldSaveCard
                            ? 'Saving Card to Account'
                            : 'Not Saving Card to Account'
                    }
                >
                    {showPurchaseGiftCard ? 'Purchase Gift Card' : 'Next'}
                </Button>
            </FlexCol>
        </FlexRow>
    );
}
